/** In-game HUD: built once, updated per frame by setting text and CSS variables only (no layout thrash). */
import { h, icon } from './dom';
import { formatTime } from './logic/format';
import { HudNumbers, litSegments } from './logic/hud';
import { LevelMap } from './minimap';
import type { LevelId } from '../sim/levels';
import type { GameUi } from './ui';

const LOGO = `${import.meta.env.BASE_URL}brand/megamotocross_logo_c.svg`;
const RPM_SEGMENTS = 18;
const GRIP_SEGMENTS = 8;

export interface HudFrame {
  n: HudNumbers;
  title: string;
  /** Main clock (drill time or current lap), null before the start. */
  time: number | null;
  limit?: number | null;
  /** e.g. "Loops 2/4", "Lap 1/3", "Checkpoint 4/12". */
  progress?: string;
  progress01?: number;
  last?: number | null;
  best?: number | null;
  waiting: boolean;
  rider?: { x: number; z: number };
}

function setText(el: HTMLElement, text: string): void {
  if (el.textContent !== text) el.textContent = text;
}

export class Hud {
  readonly el: HTMLElement;
  private readonly speed: HTMLElement;
  private readonly unit: HTMLElement;
  private readonly gear: HTMLElement;
  private readonly rpm: HTMLElement[] = [];
  private readonly grip: HTMLElement[] = [];
  private readonly gripBox: HTMLElement;
  private readonly stance: HTMLElement;
  private readonly leanDot: HTMLElement;
  private readonly title: HTMLElement;
  private readonly clock: HTMLElement;
  private readonly sub: HTMLElement;
  private readonly prog: HTMLElement;
  private readonly progBar: HTMLElement;
  private readonly splits: HTMLElement;
  private readonly waiting: HTMLElement;
  private readonly flag: HTMLElement;
  private readonly coachBox: HTMLElement;
  private readonly coachText: HTMLElement;
  private readonly toastBox: HTMLElement;
  private readonly labels: { el: HTMLElement; key: Parameters<GameUi['tr']>[0] }[] = [];
  private readonly mapBox: HTMLElement;
  private map: LevelMap | null = null;
  private readonly controlsBox: HTMLElement;
  private controlsTimer = 0;
  private controlsHideTimer = 0;
  private readonly controlLabels: { el: HTMLElement; key: Parameters<GameUi['tr']>[0] }[] = [];
  private coachTimer = 0;
  private toastTimer = 0;
  private last = '';

  constructor(private readonly ui: GameUi) {
    const label = (key: Parameters<GameUi['tr']>[0], cls = 'lbl') => { const el = h(`span.${cls}`, { text: ui.tr(key) }); this.labels.push({ el, key }); return el; };
    this.speed = h('span.speed-num', { text: '0' });
    this.unit = label('hud.kmh', 'speed-unit');
    this.gear = h('b.gear-num', { text: '1' });
    const rpmBar = h('div.rpm-bar');
    for (let i = 0; i < RPM_SEGMENTS; i++) { const s = h('i'); s.style.setProperty('--i', String(i / (RPM_SEGMENTS - 1))); rpmBar.append(s); this.rpm.push(s); }
    const gripBar = h('div.grip-bar');
    for (let i = 0; i < GRIP_SEGMENTS; i++) { const s = h('i'); gripBar.append(s); this.grip.push(s); }
    this.gripBox = h('div.hud-box.grip', {}, [label('hud.grip'), gripBar]);
    this.stance = h('div.stance-chip', { text: ui.tr('hud.seated') });
    this.leanDot = h('i.lean-dot');
    this.flag = h('div.hud-flag');
    this.title = h('div.hud-title');
    this.clock = h('div.hud-clock', { text: formatTime(0) });
    this.sub = h('div.hud-sub');
    this.progBar = h('i');
    this.prog = h('div.hud-prog', {}, [h('span.prog-txt'), h('div.prog-bar', {}, [this.progBar])]);
    this.splits = h('div.hud-splits');
    this.waiting = h('div.hud-waiting', { text: ui.tr('hud.waiting') });
    this.labels.push({ el: this.waiting, key: 'hud.waiting' });
    this.coachText = h('p');
    this.coachBox = h('div.coach-toast', { attrs: { role: 'status', 'aria-live': 'polite' } }, [
      h('div.avatar', {}, [h('img', { attrs: { src: LOGO, alt: '' } })]),
      h('div', {}, [label('hud.coach', 'coach-name'), this.coachText]),
    ]);
    this.toastBox = h('div.hud-toast', { attrs: { role: 'status', 'aria-live': 'polite' } });
    this.mapBox = h('div.hud-map.glass-hud');
    this.controlsBox = h('div.hud-controls.glass-hud');

    this.el = h('div.hud', { attrs: { 'aria-hidden': 'false' } }, [
      h('div.hud-timer.glass-hud', {}, [this.title, this.clock, this.sub, this.prog, this.splits]),
      this.coachBox,
      this.toastBox,
      this.waiting,
      this.mapBox,
      this.controlsBox,
      h('div.hud-cluster', {}, [
        h('div.hud-box.speedo.glass-hud', {}, [
          h('div.speed-row', {}, [this.speed, this.unit]),
          h('div.rpm-row', {}, [label('hud.rpm'), rpmBar]),
          h('div.gear-box', {}, [label('hud.gear'), this.gear]),
        ]),
        h('div.hud-side.glass-hud', {}, [
          this.gripBox,
          h('div.hud-box.stance', {}, [label('hud.weight'), h('div.lean-track', {}, [label('hud.weightBack', 'lean-end'), this.leanDot, label('hud.weightFwd', 'lean-end')]), this.stance]),
          this.flag,
        ]),
      ]),
    ]);
    document.body.append(this.el);
    this.setVisible(false);
  }

  setVisible(on: boolean): void {
    // Coach lines and toasts belong to the ride they came from: the timers only run while the HUD updates, so a line
    // left over from a finished ride would otherwise greet the replay or the next ride.
    const wasOn = this.el.classList.contains('on');
    if (on !== wasOn) {
      this.coachTimer = this.toastTimer = 0;
      this.coachBox.classList.remove('show');
      this.toastBox.classList.remove('show');
    }
    this.el.classList.toggle('on', on);
    // A fresh ride on a browser: the keyboard legend, briefly (touch devices have their pedals on screen).
    if (on && !wasOn) this.showControls();
  }

  /** The minimap for the loaded level. */
  setLevel(id: LevelId): void {
    this.map = new LevelMap(id, 190, 130, 'map hud-minimap');
    this.mapBox.replaceChildren(this.map.svg);
  }

  /** The compact keyboard legend shown on the right at the start of a ride (browser only). */
  private buildControls(): void {
    type K = Parameters<GameUi['tr']>[0];
    const beginner: [K, string][] = [
      ['ctrl.throttle', 'W'], ['ctrl.brakeSimplified', 'S'], ['ctrl.steer', 'A  D'], ['ctrl.leanBack', 'Q  ↓'], ['ctrl.leanForward', 'E  ↑'],
      ['ctrl.stand', 'Space'], ['ctrl.reset', 'R'], ['ctrl.camera', 'C'], ['ctrl.pause', 'Esc'],
    ];
    const pro: [K, string][] = [
      ['ctrl.throttle', 'W'], ['ctrl.frontBrake', 'Shift'], ['ctrl.rearBrake', 'S'], ['ctrl.steer', 'A  D'], ['ctrl.body', 'E  Q'],
      ['ctrl.stand', 'Space'], ['ctrl.foot', 'F'], ['ctrl.reset', 'R'], ['ctrl.camera', 'C'], ['ctrl.pause', 'Esc'],
    ];
    const rows = this.ui.settings.beginner ? beginner : pro;
    this.controlLabels.length = 0;
    const list = h('div.hud-controls-list', {}, rows.map(([key, k]) => {
      const l = h('span', { text: this.ui.tr(key) });
      this.controlLabels.push({ el: l, key });
      return h('div.hud-controls-row', {}, [l, h('kbd', { text: k })]);
    }));
    this.controlsBox.replaceChildren(h('div.hud-controls-title', { text: this.ui.tr('ui.intro.controls') }), list);
  }

  /** Shows the keyboard legend for `seconds` (no-op on touch devices, which have on-screen pedals). */
  showControls(seconds = 7): void {
    if (this.ui.touch) return;
    this.buildControls();
    this.controlsBox.classList.add('show');
    this.controlsTimer = seconds;
    // A wall-clock hide as well: the hint must fade even while the frame loop is paused (menu, replays, a background tab).
    window.clearTimeout(this.controlsHideTimer);
    this.controlsHideTimer = window.setTimeout(() => this.controlsBox.classList.remove('show'), seconds * 1000);
  }

  relabel(): void {
    for (const l of this.labels) l.el.textContent = this.ui.tr(l.key);
    for (const l of this.controlLabels) l.el.textContent = this.ui.tr(l.key);
    this.last = '';
  }

  update(f: HudFrame, dt: number): void {
    const n = f.n;
    // DOM writes only on change: an unchanged textContent write still replaces the text node and re-runs style and
    // layout for the HUD every frame.
    setText(this.speed, String(n.speedKmh));
    setText(this.gear, String(n.gear));
    const lit = litSegments(n.rpm01, RPM_SEGMENTS);
    this.rpm.forEach((s, i) => s.classList.toggle('on', i < lit));
    const g = litSegments(n.grip01, GRIP_SEGMENTS);
    this.grip.forEach((s, i) => s.classList.toggle('on', i < g));
    this.gripBox.classList.toggle('warn', n.grip01 < 0.5);
    const standing = n.stance === 'standing';
    const stanceText = this.ui.tr(standing ? 'hud.standing' : 'hud.seated');
    if (this.stance.textContent !== stanceText) this.stance.textContent = stanceText;
    this.stance.classList.toggle('up', standing);
    // Body forward (E, right stick up, +1) moves the dot right, towards the FWD end.
    this.leanDot.style.setProperty('--lean', String(n.lean));
    const flag = n.frontLock ? this.ui.tr('hud.frontLock') : n.wheelspin ? this.ui.tr('hud.slip') : n.air ? this.ui.tr('hud.air') : n.reverse ? this.ui.tr('hud.reverse') : '';
    if (this.flag.textContent !== flag) { this.flag.textContent = flag; this.flag.className = `hud-flag${flag ? ' show' : ''}${n.air && !n.frontLock && !n.wheelspin ? ' air' : ''}`; }

    const key = `${f.title}|${f.progress ?? ''}|${f.limit ?? ''}|${f.last ?? ''}|${f.best ?? ''}|${f.waiting}`;
    if (key !== this.last) {
      this.last = key;
      this.title.textContent = f.title;
      this.sub.textContent = f.limit ? this.ui.tr('hud.limit', { time: formatTime(f.limit) }) : '';
      (this.prog.firstChild as HTMLElement).textContent = f.progress ?? '';
      this.prog.style.display = f.progress ? '' : 'none';
      this.splits.replaceChildren(...[
        f.last !== undefined && f.last !== null ? h('span', {}, [h('small', { text: this.ui.tr('hud.last') }), h('b', { text: formatTime(f.last) })]) : null,
        f.best !== undefined && f.best !== null ? h('span.best', {}, [h('small', { text: this.ui.tr('hud.best') }), h('b', { text: formatTime(f.best) })]) : null,
      ].filter((x): x is HTMLElement => x !== null));
      this.waiting.classList.toggle('show', f.waiting);
    }
    const bar = `${Math.round((f.progress01 ?? 0) * 100)}%`;
    if (this.progBar.style.width !== bar) this.progBar.style.width = bar;
    setText(this.clock, formatTime(f.time ?? 0));
    if (f.rider) this.map?.setRider(f.rider.x, f.rider.z);

    if (this.coachTimer > 0 && (this.coachTimer -= dt) <= 0) this.coachBox.classList.remove('show');
    if (this.toastTimer > 0 && (this.toastTimer -= dt) <= 0) this.toastBox.classList.remove('show');
    if (this.controlsTimer > 0 && (this.controlsTimer -= dt) <= 0) this.controlsBox.classList.remove('show');
  }

  coach(text: string): void {
    this.coachText.textContent = text;
    this.coachBox.classList.remove('show');
    void this.coachBox.offsetWidth; // restart the entry animation
    this.coachBox.classList.add('show');
    this.coachTimer = 3.2;
  }

  toast(text: string, kind: 'info' | 'good' | 'bad'): void {
    this.toastBox.replaceChildren(icon(kind === 'bad' ? 'info' : kind === 'good' ? 'trophy' : 'flag'), h('span', { text }));
    this.toastBox.className = `hud-toast ${kind}`;
    void this.toastBox.offsetWidth;
    this.toastBox.classList.add('show');
    this.toastTimer = 2.4;
  }
}
